/**
 * GWP — Gift With Purchase
 *
 * Strategy:
 * - Reads config from <meta> tags rendered by snippets/gwp-meta.liquid
 * - Fetches eligible product IDs from qualifying collections via /collections/{handle}/products.json
 * - Caches eligible product IDs in memory (sessionStorage for persistence across soft navigations)
 * - On every cart:update — sums only qualifying line items by product ID match
 * - Auto-adds GWP variant when qualifying subtotal >= threshold
 * - Auto-removes GWP variant when qualifying subtotal < threshold
 * - Hides remove button and locks qty stepper on GWP line item
 */

(function () {
  'use strict';

  /* ============================================================
     1. READ CONFIG FROM META TAGS
  ============================================================ */

  const GWP_VARIANT_ID   = document.querySelector('meta[name="gwp-variant-id"]')?.content;
  const GWP_PIM_PRODUCT_ID = document.querySelector('meta[name="gwp-pim-product-id"]')?.content || '';
  const GWP_THRESHOLD    = parseInt(document.querySelector('meta[name="gwp-threshold"]')?.content || '0', 10);
  const GWP_COLLECTIONS  = (document.querySelector('meta[name="gwp-eligible-collections"]')?.content || '')
    .split(',').map(h => h.trim()).filter(Boolean);

  // Exit early if not configured for this market
  if (!GWP_VARIANT_ID || GWP_THRESHOLD === 0 || GWP_COLLECTIONS.length === 0) {
    console.log('[GWP] Not configured for this market — exiting.');
    return;
  }

  console.log('[GWP] Initialised:', { variantId: GWP_VARIANT_ID, threshold: GWP_THRESHOLD, collections: GWP_COLLECTIONS });

  /* ============================================================
     2. CART ROUTE HELPERS
  ============================================================ */

  const root      = (window.Theme?.routes?.root_url ?? '/').replace(/\/+$/, '');
  const cartUrl   = root + '/cart.js';
  const addUrl    = root + '/cart/add.js';
  const changeUrl = root + '/cart/change.js';

  /* ============================================================
     3. CACHE KEYS
     Store eligible product IDs in sessionStorage so we don't
     re-fetch collections on every cart update
  ============================================================ */

  const CACHE_KEY         = 'gwp_eligible_ids_' + GWP_COLLECTIONS.join('_');
  const CACHE_VERSION_KEY = 'gwp_eligible_ids_version';
  const CACHE_VERSION     = GWP_COLLECTIONS.join(','); // Bust cache if collections change

  /* ============================================================
     4. FETCH ELIGIBLE PRODUCT IDs FROM COLLECTIONS
     Fetches all pages of each collection and merges product IDs
  ============================================================ */

  async function fetchEligibleProductIds() {
    // Check cache first
    try {
      const cachedVersion = sessionStorage.getItem(CACHE_VERSION_KEY);
      const cachedIds     = sessionStorage.getItem(CACHE_KEY);

      if (cachedVersion === CACHE_VERSION && cachedIds) {
        const parsed = JSON.parse(cachedIds);
        console.log('[GWP] Using cached eligible product IDs:', parsed.length);
        return new Set(parsed);
      }
    } catch (e) {
      // sessionStorage unavailable — continue without cache
    }

    console.log('[GWP] Fetching eligible product IDs from collections...');
    const eligibleIds = new Set();

    for (const handle of GWP_COLLECTIONS) {
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        try {
          const url = `${root}/collections/${handle}/products.json?limit=250&page=${page}`;
          const res  = await fetch(url);
          if (!res.ok) { console.warn(`[GWP] Could not fetch collection: ${handle}`); break; }

          const data = await res.json();
          const products = data.products || [];

          products.forEach(p => eligibleIds.add(p.id));

          // If we got fewer than 250, no more pages
          hasMore = products.length === 250;
          page++;
        } catch (err) {
          console.error(`[GWP] Error fetching collection ${handle}:`, err);
          break;
        }
      }
    }

    console.log('[GWP] Total eligible product IDs fetched:', eligibleIds.size);

    // Cache the result
    try {
      sessionStorage.setItem(CACHE_VERSION_KEY, CACHE_VERSION);
      sessionStorage.setItem(CACHE_KEY, JSON.stringify([...eligibleIds]));
    } catch (e) {
      // sessionStorage unavailable — skip cache
    }

    return eligibleIds;
  }

  /* ============================================================
     5. FETCH CURRENT CART STATE
  ============================================================ */

  async function fetchCart() {
    try {
      const res = await fetch(cartUrl);
      return await res.json();
    } catch (err) {
      console.error('[GWP] Failed to fetch cart:', err);
      return null;
    }
  }

  /* ============================================================
     6. CALCULATE QUALIFYING SUBTOTAL
     Only sums line items whose product_id is in eligible set
     Excludes the GWP item itself from the subtotal
  ============================================================ */

  function getQualifyingSubtotal(cartItems, eligibleIds) {
    return cartItems.reduce((sum, item) => {
      const isGwp       = String(item.variant_id) === String(GWP_VARIANT_ID);
      const isEligible  = eligibleIds.has(item.product_id);
      return (!isGwp && isEligible) ? sum + item.final_line_price : sum;
    }, 0);
  }

  /* ============================================================
     7. CHECK IF GWP IS ALREADY IN CART
  ============================================================ */

  function getGwpItem(cartItems) {
    return cartItems.find(item => String(item.variant_id) === String(GWP_VARIANT_ID));
  }

  /* ============================================================
     8. ADD GWP TO CART
  ============================================================ */

  async function addGwp() {
    try {
      console.log('[GWP] Adding GWP to cart...');
      const res = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: GWP_VARIANT_ID,
          quantity: 1,
          properties: GWP_PIM_PRODUCT_ID
            ? { _gwp: 'true', _pim_product_id: GWP_PIM_PRODUCT_ID }
            : { _gwp: 'true' }
        })
      });
      if (!res.ok) throw new Error('Add failed: ' + res.status);
      console.log('[GWP] GWP added successfully.');
      return true;
    } catch (err) {
      console.error('[GWP] Failed to add GWP:', err);
      return false;
    }
  }

  /* ============================================================
     9. REMOVE GWP FROM CART
  ============================================================ */

  async function removeGwp(lineKey) {
    try {
      console.log('[GWP] Removing GWP from cart...');
      const res = await fetch(changeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 })
      });
      if (!res.ok) throw new Error('Remove failed: ' + res.status);
      console.log('[GWP] GWP removed successfully.');
      return true;
    } catch (err) {
      console.error('[GWP] Failed to remove GWP:', err);
      return false;
    }
  }

  /* ============================================================
     10. MARK GWP ITEMS IN DOM
     Adds data-gwp="true" so CSS hides remove button
  ============================================================ */

  function markGwpItems() {
    // Mini cart drawer items
    document.querySelectorAll(`.mini-cart-item[data-variant-id="${GWP_VARIANT_ID}"]`).forEach(el => {
      el.setAttribute('data-gwp', 'true');
    });

    // Cart page rows
    document.querySelectorAll(`.sb-row[data-variant-id="${GWP_VARIANT_ID}"]`).forEach(el => {
      el.setAttribute('data-gwp', 'true');
    });
  }

  /* ============================================================
     11. TRIGGER MINI CART DRAWER RE-RENDER
     Dispatches cart:update so mini-cart-drawer.js re-fetches
  ============================================================ */

  function triggerDrawerRerender() {
    document.dispatchEvent(new CustomEvent('cart:update', {
      detail: { data: { didError: false } }
    }));
    observeDrawerRender();
  }

  /* ============================================================
     12. OBSERVE MINI CART DRAWER RE-RENDER
     MutationObserver waits for drawer HTML to update
     then marks the GWP item
  ============================================================ */

  function observeDrawerRender() {
    const container = document.getElementById('mini-cart-items-container');
    if (!container) return;

    const observer = new MutationObserver(() => {
      observer.disconnect();
      markGwpItems();
    });

    observer.observe(container, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 3000); // Safety fallback
  }

  /* ============================================================
     13. DEBOUNCE HELPER
     Prevents rapid-fire evaluations on quick cart changes
  ============================================================ */

  let debounceTimer = null;

  function debounce(fn, delay = 400) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(fn, delay);
  }

  /* ============================================================
     14. CORE EVALUATION LOGIC
     Fetches eligible IDs + cart state, then decides add/remove
  ============================================================ */

  let isEvaluating = false;

  async function evaluateGwp({ rerenderDrawer = false, reloadPage = false } = {}) {
    if (isEvaluating) return;
    isEvaluating = true;

    try {
      // Fetch eligible product IDs and cart in parallel
      const [eligibleIds, cart] = await Promise.all([
        fetchEligibleProductIds(),
        fetchCart()
      ]);

      if (!cart || !eligibleIds) return;

      const qualifyingSubtotal = getQualifyingSubtotal(cart.items, eligibleIds);
      const gwpItem            = getGwpItem(cart.items);
      const thresholdMet       = qualifyingSubtotal >= GWP_THRESHOLD;

      console.log('[GWP] Qualifying subtotal:', qualifyingSubtotal, '| Threshold:', GWP_THRESHOLD, '| Met:', thresholdMet, '| GWP in cart:', !!gwpItem);

      if (thresholdMet && !gwpItem) {
        // Add GWP
        const added = await addGwp();
        if (added) {
          if (rerenderDrawer) triggerDrawerRerender();
          if (reloadPage) window.location.reload();
        }
      } else if (!thresholdMet && gwpItem) {
        // Remove GWP
        const removed = await removeGwp(gwpItem.key);
        if (removed) {
          if (rerenderDrawer) triggerDrawerRerender();
          if (reloadPage) window.location.reload();
        }
      } else {
        // No change needed — just mark DOM
        markGwpItems();
      }
    } finally {
      isEvaluating = false;
    }
  }

  /* ============================================================
     15. LISTEN FOR CART UPDATES (MINI CART DRAWER)
     cart:update is fired by the theme's add-to-cart flow
     We debounce to avoid evaluating before the cart settles
  ============================================================ */

  document.addEventListener('cart:update', () => {
  debounce(() => {
    evaluateGwp({ rerenderDrawer: true }).then(() => {
      observeDrawerRender();
    });
  }, 400);
});

  /* ============================================================
     16. CART PAGE HANDLING
     Cart page reloads after every change (window.location.reload)
     so we evaluate once on load, then mark GWP items
  ============================================================ */

  function isCartPage() {
    return window.location.pathname.includes('/cart');
  }

  if (isCartPage()) {
    evaluateGwp({ reloadPage: true }).then(() => {
      markGwpItems();
    });
  }

  /* ============================================================
     17. INITIAL SILENT EVALUATION ON ALL OTHER PAGES
     Cleans up any inconsistent cart state on page load
     (e.g. GWP in cart but threshold no longer met)
  ============================================================ */

  if (!isCartPage()) {
  evaluateGwp({ rerenderDrawer: false, reloadPage: false }).then(() => {
    // Watch for drawer opening after initial evaluation
    observeDrawerRender();
  });
}

/* ============================================================
   18. OBSERVE CART ICON CLICKS
   Marks GWP items every time the mini cart drawer opens
============================================================ */

document.querySelectorAll('[data-open-mini-cart]').forEach(icon => {
  icon.addEventListener('click', () => {
    // Wait for drawer to render then mark GWP items
    setTimeout(() => {
      observeDrawerRender();
      markGwpItems();
    }, 500);
  });
});

/* ============================================================
   19. LISTEN FOR MINI CART RE-RENDER
   Marks GWP items every time the drawer HTML refreshes
============================================================ */
document.addEventListener('mini-cart:rendered', () => {
  markGwpItems();
});

})();